import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Save, AlertCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { useNotes } from '../../contexts/NotesContext';
import { notesApi } from '../../services/cloudApi';

const SaveSharedNoteModal = ({ isOpen, onClose, message, groupId }) => {
  const { t } = useTranslation('notes');
  const { authToken } = useAuth();
  const notesContext = useNotes();
  const createNoteFromShared = notesContext?.createNoteFromShared;
  const [isSaving, setIsSaving] = useState(false);
  const [isDuplicate, setIsDuplicate] = useState(false);

  // DEBUG: Log message payload
  useEffect(() => {
    if (isOpen && message) {
      console.log('[SaveSharedNoteModal] Message payload:', {
        message,
        note_title: message?.note_title,
        noteTitle: message?.noteTitle,
        note_content: message?.note_content,
        noteContent: message?.noteContent,
        note_title_color: message?.note_title_color,
        noteTitleColor: message?.noteTitleColor,
      });
    }
  }, [isOpen, message]);

  // Lock body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Handle ESC key
  useEffect(() => {
    if (!isOpen) return;
    
    const handleEsc = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [isOpen, onClose]);

  const handleSave = async () => {
    if (!message || !authToken) return;

    // Extract note data from message
    const noteTitle = message?.note_title || message?.noteTitle || '';
    const noteContent = message?.note_content || message?.noteContent || '';
    const noteTitleColor = message?.note_title_color || message?.noteTitleColor || '#ffffff';

    console.log('[SaveSharedNoteModal] Saving note:', {
      noteTitle,
      noteContentLength: noteContent?.length || 0,
      noteTitleColor,
    });

    setIsSaving(true);
    setIsDuplicate(false);
    
    try {
      // OPTIMISTIC UI: Add note to context immediately
      // This will update sidebar instantly - NO WAITING
      if (createNoteFromShared) {
        try {
          await createNoteFromShared({
            title: noteTitle || t('common:unnamed'),
            content: noteContent || '',
            titleColor: noteTitleColor,
          });
          console.log('[SaveSharedNoteModal] ✅ Optimistic note added to sidebar - UI updated instantly');
        } catch (optimisticError) {
          console.error('[SaveSharedNoteModal] Optimistic UI error:', optimisticError);
          // If optimistic fails (e.g., limit exceeded), still try backend
          // Backend will return proper error
        }
      }

      // Backend save (updates optimistic note with real ID from DB)
      // This is async but doesn't block UI - optimistic note already visible
      const response = await notesApi.saveShared({
        token: authToken,
        messageId: message.id,
        groupId: groupId || null,
      });

      if (response?.success) {
        // Show success toast
        const successMessage = response.isDuplicate
          ? t('saveSharedModal.successDuplicate')
          : (response.message || t('saveSharedModal.successDefault'));

        window.dispatchEvent(
          new CustomEvent('notesplus-show-toast', {
            detail: {
              type: 'success',
              title: t('common:success'),
              message: successMessage,
            },
          })
        );

        // Close modal
        onClose();
      }
    } catch (error) {
      console.error('[SaveSharedNoteModal] Save error:', error);
      
      // Extract error message
      let errorMessage = t('saveSharedModal.errorDefault');

      if (error?.payload) {
        const payload = error.payload;
        if (payload.message) {
          errorMessage = payload.message;
        } else if (payload.error) {
          const errorCode = payload.error;
          if (errorCode === 'MESSAGE_NOT_FOUND') {
            errorMessage = t('saveSharedModal.errorMessageNotFound');
          } else if (errorCode === 'NOT_GROUP_MEMBER') {
            errorMessage = t('saveSharedModal.errorNotGroupMember');
          } else if (errorCode === 'INVALID_MESSAGE') {
            errorMessage = t('saveSharedModal.errorInvalidMessage');
          } else if (errorCode === 'UNAUTHORIZED') {
            errorMessage = t('saveSharedModal.errorUnauthorized');
          } else {
            errorMessage = payload.message || errorMessage;
          }
        }
      } else if (error?.message) {
        errorMessage = error.message;
      }

      window.dispatchEvent(
        new CustomEvent('notesplus-show-toast', {
          detail: {
            type: 'error',
            title: t('common:errorTitle'),
            message: errorMessage,
          },
        })
      );
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen || !message) return null;

  // Get note title with proper fallback
  const noteTitle = message.note_title || message.noteTitle;
  const displayTitle = noteTitle && noteTitle.trim() ? noteTitle.trim() : t('common:unnamed');

  if (!isOpen || !message) return null;

  // Use portal for proper z-index layering
  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop - Full screen overlay */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 z-[100] bg-slate-900/30 backdrop-blur-sm flex items-center justify-center"
            style={{ pointerEvents: 'auto' }}
          />

          {/* Modal Container */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="fixed inset-0 z-[1000000] flex items-center justify-center p-4 overflow-y-auto pointer-events-none"
            onClick={onClose}
            style={{ pointerEvents: 'auto' }}
          >
            <div
              className={`bg-[var(--color-bg-base)] dark:bg-[var(--color-bg-elevated)] rounded-2xl shadow-2xl w-full max-w-md border border-white/10 dark:border-white/10`}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex items-center justify-between p-4 border-b border-white/10 dark:border-white/10">
                <h3 className="text-lg font-semibold text-[var(--color-text-primary)] dark:text-white">
                  {t('saveSharedModal.title')}
                </h3>
                <button
                  onClick={onClose}
                  className="p-1.5 rounded-lg text-[var(--color-text-muted)] dark:text-[var(--color-text-muted)] hover:bg-[var(--color-bg-elevated)] dark:hover:bg-[#1e2d47] transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Content */}
              <div className="p-4">
                <p className="text-sm text-[var(--color-text-secondary)] dark:text-gray-300 mb-4">
                  {t('saveSharedModal.confirmText')}
                </p>
                
                {/* Note Preview */}
                <div className="p-3 bg-[var(--color-bg-muted)] dark:bg-[#161f31] rounded-lg mb-4">
                  <p className="text-sm font-medium text-[var(--color-text-primary)] dark:text-white break-words">
                    {displayTitle}
                  </p>
                </div>
              </div>

              {/* Footer */}
              <div className="p-4 border-t border-white/10 dark:border-white/10 flex items-center justify-end gap-3">
                <button
                  onClick={onClose}
                  className="px-4 py-2 text-sm font-medium text-[var(--color-text-secondary)] dark:text-gray-300 hover:bg-[var(--color-bg-elevated)] dark:hover:bg-[#1e2d47] rounded-lg transition-colors"
                  disabled={isSaving}
                >
                  {t('common:cancel')}
                </button>
                <button
                  onClick={handleSave}
                  disabled={isSaving}
                  className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors flex items-center gap-2 ${
                    !isSaving
                      ? 'bg-blue-600 hover:bg-blue-700 text-white'
                      : 'bg-gray-300 dark:bg-[#161f31] text-[var(--color-text-muted)] dark:text-[var(--color-text-muted)] cursor-not-allowed'
                  }`}
                >
                  {isSaving ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      {t('common:saving')}
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4" />
                      {t('common:save')}
                    </>
                  )}
                </button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>,
    document.body
  );
};

export default SaveSharedNoteModal;
